import useToasts from '@leaflink/stash/useToasts';
import { useQueryClient } from '@tanstack/vue-query';
import { storeToRefs } from 'pinia';
import { ActionContext } from 'vuex';

import { ORDER_RETURN_REVERSE_TRANSITIONS, SPLIT_NAMES, STATUSES } from '@/constants';
import { OrderSummaryComment } from '@/models/customer';
import { InventoryFacility } from '@/models/inventory';
import {
  Order,
  OrderDocument,
  OrderDocumentDelete,
  OrderDocumentPayload,
  OrderPayment,
  OrderReturn,
} from '@/models/order';
import { RootState } from '@/models/vuex/rootState';
import notify from '@/notify';
import { useCompanyStore } from '@/piniaStores/company/company.store';
import { useUserStore } from '@/piniaStores/user/user.store';
import i18n from '@/plugins/i18n';
import { pricingQueryKeys } from '@/queries/pricing/pricing.query';
import api from '@/services/api/';
import { UpdateOrderDeliveredPayload } from '@/services/api/order';
import { OrderState } from '@/store/orders/state';
import { handleError } from '@/utils/errors';
import JSONTransform from '@/utils/json-transform';

import OrderStore from './Order';

const toasts = useToasts();

/**
 * Assembles an Order so that it has all required fields, properties, and data prior to it
 * being persisted in the Store. We need to assemble `taxes` and `lineItems` (which have
 * their own required child `products`.
 * @param payload
 * @param payload.data - An Order response from the API. It lacks tax and product information.
 * @param payload.isBrandOwner - Optional flag for BME Orders
 * @return A fully hydrated Order object.
 */
async function assembleOrder({
  data,
  isBrandOwner,
}: {
  // The orders types need a lot of help
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any; // TODO: use specific type, address in LEAF-8277
  isBrandOwner?: boolean;
  isInventoryOverviewEnabled?: boolean;
}) {
  const uuid = data.uuid || data.orderNumber || data.number;

  if (!uuid) {
    notify.error(i18n.t('order.notify.missingUuid'));
    throw new Error(`Invalid uuid: ${uuid}`);
  }

  data.taxes = await api.order.getOrderTaxes({ uuid, isBrandOwner });

  if (data.lineItems) {
    //
    // ------ addresses inconsistency with API response. [TODO] -------
    data.lineItems.forEach((item) => (item.productId = item.productId || item.product || ''));
    // ------- end api hack -------------------
    //

    const productIds = data.lineItems.map((item) => item.productId).join(',');
    const products = await api.inventory.getProducts({
      id: productIds,
      isBrandOwner,
    });

    data.lineItems.forEach((item) => {
      item.product = products.find((p) => p.id === item.productId);
    });
  } else {
    data.lineItems = []; // TODO, why missing from the response?
  }

  return new OrderStore(data);
}

/**
 * Fetches a list of orders received; fetches a single order received when uuid is passed
 * TODO: rename to `fetchOrdersReceived`
 * @param {object} [data] Object with params to pass
 * @param {object} [data.uuid] uuid is passed when fetching only one order
 */
export async function fetchOrders({ commit }, data: { uuid?: string } = {}) {
  const { uuid } = data;
  let order;
  let response;

  const { getIsSplitEnabled } = storeToRefs(useUserStore());

  try {
    commit('FETCHING_ORDERS', true);
    response = await api.order.getOrders(data);

    const isInventoryOverviewEnabled = getIsSplitEnabled.value(SPLIT_NAMES.INVENTORY_FACILITY);

    if (uuid) {
      order = await assembleOrder({
        data: response,
        isInventoryOverviewEnabled,
      });
      commit('SET_ORDER', order);
    } else {
      commit(
        'SET_ORDERS',
        response.map((o) => new OrderStore(o)),
      );
    }
  } finally {
    commit('FETCHING_ORDERS', false);
  }

  return order || response;
}

/**
 * Fetch a single BME order
 * @param {string} uuid of the order
 */
export async function fetchBMEOrder({ commit }, uuid: string) {
  let order: Order;

  const { getIsSplitEnabled } = storeToRefs(useUserStore());

  try {
    commit('FETCHING_ORDERS', true);

    const isInventoryOverviewEnabled = getIsSplitEnabled.value(SPLIT_NAMES.INVENTORY_FACILITY);

    const response = await api.order.getBMEOrder(uuid);

    order = await assembleOrder({
      data: response,
      isBrandOwner: true,
      isInventoryOverviewEnabled,
    });

    commit('SET_ORDER', order);
  } finally {
    commit('FETCHING_ORDERS', false);
  }

  return order;
}

/**
 * Fetches line items of an order
 * @param {string} uuid number of the order
 * @return a list of line items
 */
export async function fetchOrderLineItems(_obj, uuid: string) {
  const response = await api.order.getOrderLineItems(uuid);

  return response;
}

/**
 * Fetches line items of a BME order
 * @param {string} uuid number of the order
 * @return a list of line items
 */
export async function fetchBMEOrderLineItems(_obj, uuid: string) {
  const response = await api.order.getBMEOrderLineItems(uuid);

  return response.results;
}

/**
 * Creates a new Draft Order.
 * @param {ActionContext} context passed by vuex
 * @param {Object} data The data needed to create an order, it's including optional isBrandOwner param for BME Orders
 * @return A fully hydrated Order object.
 */
export async function createOrder({ commit }, data) {
  const { isBrandOwner } = data;
  const response = await api.order
    .createOrder(data)
    .catch((e) => handleError(e, i18n.t('orders.notify.creatingOrder')));

  const { getIsSplitEnabled } = storeToRefs(useUserStore());

  const isInventoryOverviewEnabled = getIsSplitEnabled.value(SPLIT_NAMES.INVENTORY_FACILITY);

  const order = await assembleOrder({
    data: response,
    isBrandOwner,
    isInventoryOverviewEnabled,
  });

  commit('SET_ORDER', order);

  return order;
}

/**
 * Updates a manual order.
 * @param {ActionContext} context passed by vuex
 * @param {Object} data An object containing any fields to update within the Order.
 * @return A fully hydrated Order object.
 */
// The orders types need a lot of help
export async function updateManualOrder(
  { commit, dispatch },
  // TODO: useQueryClient() cannot be called within Vuex actions so passing in the queryClient is necessary until this Vuex store is replaced with Vue Query composables.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  { data, queryClient }: { data: any; queryClient: ReturnType<typeof useQueryClient> },
) {
  const { creditsApplied, creditsToRemove, ...orderData } = data;
  const response = await api.order.updateOrder(orderData);

  const { getIsSplitEnabled } = storeToRefs(useUserStore());

  const isInventoryOverviewEnabled = getIsSplitEnabled.value(SPLIT_NAMES.INVENTORY_FACILITY);

  const updatedOrder = await assembleOrder({
    data: response,
    isInventoryOverviewEnabled,
  });

  commit('SET_ORDER', { ...updatedOrder, creditsApplied });

  queryClient.invalidateQueries({
    queryKey: pricingQueryKeys.appliedVolumeDiscountsByOrderId(updatedOrder.uuid),
  });

  //if the order is already submitted, apply/remove credits from the order
  //otherwise credits will be updated in OrderConfirm
  if (response.status?.toLowerCase() !== STATUSES.ORDER.DRAFT.name) {
    // There's a scenario where a race condition can occur if the order is updated
    // and credits are saved at the same time.
    // This may result in the order having an incorrect total.
    // To prevent this, we send these credits in sequence, not in parallel.

    // Process creditsToRemove first
    for (const credit of creditsToRemove) {
      await dispatch('CRM/saveCredit', credit, { root: true });
    }

    // Then process creditsApplied
    for (const credit of creditsApplied) {
      if (!credit.readOnly) {
        await dispatch('CRM/saveCredit', credit, { root: true });
      }
    }
  }

  return updatedOrder;
}

/**
 * Updates an Order.
 * @param {Object} data An object containing any fields to update within the Order.
 * @return A fully hydrated Order object.
 */
export async function updateCollabOrdersReceived(_obj, data: Order) {
  const response = await api.order.updateCollabOrdersReceived(data);

  return response;
}

/**
 * Updates an Order.
 * @param {Object} data An object containing any fields to update within the Order.
 * @return A fully hydrated Order object.
 */
// The orders types need a lot of help
// eslint-disable-next-line require-await, @typescript-eslint/no-explicit-any
export async function updateOrder(_obj, data: any) {
  return api.order.updateOrder(data);
}

/**
 * Updates a BME Order.
 * @param {Object} data An object containing any fields to update within the Order.
 * @return A fully hydrated Order object.
 */
export async function updateBMEOrder(
  { commit },
  // TODO: useQueryClient() cannot be called within Vuex actions so passing in the queryClient is necessary until this Vuex store is replaced with Vue Query composables.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  { data, queryClient }: { data: any; queryClient: ReturnType<typeof useQueryClient> },
) {
  const response = await api.order.updateBMEOrder(data);

  const { getIsSplitEnabled } = storeToRefs(useUserStore());

  const isInventoryOverviewEnabled = getIsSplitEnabled.value(SPLIT_NAMES.INVENTORY_FACILITY);

  const updatedOrder = await assembleOrder({
    data: response,
    isBrandOwner: true,
    isInventoryOverviewEnabled,
  });

  commit('SET_ORDER', updatedOrder);

  queryClient.invalidateQueries({ queryKey: pricingQueryKeys.appliedVolumeDiscountsByOrderId(data.uuid) });

  return updatedOrder;
}

/**
 * Submits an Order.
 * @param {data} uuid and credits of the order
 */
export async function submitOrder(
  { commit, dispatch },
  // TODO: useQueryClient() cannot be called within Vuex actions so passing in the queryClient is necessary until this Vuex store is replaced with Vue Query composables.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  { data, queryClient }: { data: any; queryClient: ReturnType<typeof useQueryClient> },
) {
  const { credits = [] } = data;
  const response = await api.order.transitionOrder({
    uuid: data.uuid,
    state: STATUSES.ORDER.SUBMITTED.transitionName,
  });

  const parsedOrder = new OrderStore(response.data);

  await Promise.all(
    credits.map((credit) => {
      credit.appliedTo = data.uuid;
      return dispatch('CRM/saveCredit', credit, { root: true });
    }),
  );

  commit('SET_ORDER', parsedOrder);

  queryClient.invalidateQueries({ queryKey: pricingQueryKeys.appliedVolumeDiscountsByOrderId(data.uuid) });

  return parsedOrder;
}

/**
 * Deletes an order
 * @param {string} uuid of the order to delete
 */
// eslint-disable-next-line require-await
export async function removeOrder(_obj, uuid: string) {
  return api.order.removeOrder(uuid);
}

/**
 * Deletes a BME order
 * @param {string} uuid of the order to delete
 */
// eslint-disable-next-line require-await
export async function removeBMEOrder(_obj, uuid: string) {
  return api.order.removeBMEOrder(uuid);
}

/**
 * Transitions an collab orders received
 * @param data
 * @param data.fromStatusName current status of the order
 * @param data.collabOrderNumber of the order, used for toaster message
 * @param data.toStatusName name of the transitioned state
 * @param data.state to which the order will be transitioned
 * @param data.uuid of the order to be transitioned
 */
export async function transitionCollabOrdersReceived(
  _,
  data: {
    fromStatusName: string;
    collabOrderNumber: string;
    toStatusName: string;
    state: string;
    uuid: string;
  },
) {
  const companyStore = useCompanyStore();

  const { fromStatusName: _fromStatusName, collabOrderNumber, toStatusName, ...params } = data;
  const response = await api.order.transitionOrder(params).catch((error) => {
    const customMessage = i18n.t('orders.notify.errorUpdateOrder', {
      number: collabOrderNumber,
    });

    handleError(error, customMessage);
  });

  companyStore.badgeCounts.collaborativeOrders--;
  companyStore.badgeCounts.submittedOrders++;

  toasts.success(
    i18n.t('orders.notify.orderUpdated', {
      number: collabOrderNumber,
      orderStatusName: toStatusName,
    }),
  );

  return response?.data;
}

/**
 * Transitions an order
 * @param {string} [data.fromStatusName] current status of the order
 * @param {string} [data.orderSellerNumber] of the order, used for toaster message
 * @param {string} [data.orderStateName] name of the transitioned state
 * @param {string} [data.state] to which the order will be transitioned
 * @param {string} [data.uuid] of the order to be transitioned
 */
export async function transitionOrder(
  _,
  data: {
    fromStatusName: string;
    orderSellerNumber: string;
    toStatusName: string;
    state: string;
    uuid: string;
  },
) {
  const { fromStatusName, orderSellerNumber, toStatusName, ...params } = data;

  const response = await api.order.transitionOrder(params).catch((error) => {
    const customMessage = i18n.t('orders.notify.errorUpdateOrder', {
      number: orderSellerNumber,
    });

    handleError(error, customMessage);
  });
  const isSubmitted = fromStatusName === STATUSES.ORDER.SUBMITTED.name;
  const isAcceptedOrRejected = [STATUSES.ORDER.ACCEPTED.name, STATUSES.ORDER.REJECTED.name].includes(toStatusName);

  // decrease the badge count if the order is being transitioned from submitted
  // to either accepted or rejected
  if (isSubmitted && isAcceptedOrRejected) {
    const companyStore = useCompanyStore();

    companyStore.badgeCounts.submittedOrders--;
  }

  toasts.success(
    i18n.t('orders.notify.orderUpdated', {
      number: orderSellerNumber,
      orderStatusName: toStatusName,
    }),
  );

  return response?.data;
}

/**
 * Combines multiple orders
 * @param {string[]} [data.uuids] of the orders to be combined
 */
export async function combineOrders(_obj, data: { uuids: string[] }) {
  const response = await api.order.combineOrders(data);

  return response.data;
}

/**
 * Adds a tax to an Order
 * @param {ActionContext} context passed by vuex
 * @param {Tax} tax The tax to add to an Order
 */
export async function addTax({ commit }, { isBrandOwner, tax }) {
  const payload = {
    isBrandOwner,
    tax: { tax: tax.taxId, order: tax.order },
  };
  const response = await api.order.addTax(payload);

  commit('ADD_TAX', response);
  return response;
}

/**
 * Removes a tax from an Order
 * @param {ActionContext} context passed by vuex
 * @param {Tax} tax The tax to remove from an Order
 */
// eslint-disable-next-line require-await
export async function removeTax({ commit }, { isBrandOwner, tax }) {
  const payload = {
    isBrandOwner,
    tax: { id: tax.id, order: tax.order },
  };

  return api.order.removeTax(payload).then(() => {
    commit('REMOVE_TAX', tax);
  });
}

/**
 * Updates a tax on an Order
 * @param {ActionContext} context passed by vuex
 * @param {Tax} tax The tax to update
 */
// eslint-disable-next-line require-await
export async function updateTax({ commit }, { isBrandOwner, tax }) {
  const payload = {
    isBrandOwner,
    tax: { id: tax.id, rate: tax.rate },
  };
  const response = api.order.updateTax(payload);

  commit('UPDATE_TAX', response);
  return response;
}

/**
 * Gets a list of returns and adds it to the orders store
 * @param {object} data Object with params to pass
 * @param {string} [data.expand] list of fields to get expanded data
 */
export async function fetchOrderReturns(
  { commit },
  data?: {
    expand?: [];
  },
) {
  commit('LOADING_ORDER_RETURNS', true);

  try {
    const response = await api.order.getOrderReturns(data);

    commit('SET_ORDER_RETURNS', response);
  } finally {
    commit('LOADING_ORDER_RETURNS', false);
  }
}

/**
 * Gets and returns a single return but does not store it in the orders store
 * @param {object} data Object with params to pass
 * @param {object} data.uuid uuid is passed when loading only one return
 * @param {string[]} [data.expand] list of fields to get expanded data
 * @returns an instance of an OrderReturn
 */
export async function fetchOrderReturn(
  { commit },
  data: {
    uuid: string;
    expand?: string[];
  },
) {
  commit('LOADING_ORDER_RETURNS', true);

  data.expand = ['returned_products'].concat(data.expand || []);

  try {
    const returnsList = await api.order.getOrderReturns(data);

    return returnsList[0];
  } finally {
    commit('LOADING_ORDER_RETURNS', false);
  }
}

/**
 * Submits the return order
 * @param {OrderReturn} data The return order to be submitted
 */
export async function submitOrderReturn(_, data: OrderReturn & { expand?: string[] }) {
  const response = await api.order.submitOrderReturn(data);

  const companyStore = useCompanyStore();

  companyStore.badgeCounts.submittedReturns++;
  toasts.success(i18n.t('orders.notify.orderReturnSubmitted'));

  return response.data;
}

/**
 * Overwrites a whole return object (sets the returned_products too, so be sure to provide them)
 * @param {object} data Object with query params
 * @param {OrderReturn} data.orderReturn the return data (returned_products will be deleted if not provided)
 * @param {string[]} [data.expand] list of fields to get expanded data
 * @return an instance of OrderReturn
 */
export async function updateOrderReturn(
  { commit },
  data: {
    orderReturn: OrderReturn;
    expand?: string[];
  },
) {
  const response = await api.order
    .updateOrderReturn(data)
    .catch((e) => handleError(e, i18n.t('orders.notify.errorUpdateReturn')));

  commit('SAVE_ORDER_RETURN', response);
  toasts.success(i18n.t('orders.notify.orderReturnUpdated'));

  return response;
}

/**
 * Updates a return, partially
 * @param {object} data Object with query params
 * @param {object} data the return data to update
 * @param {string} data.uuid the id of the return being updated
 * @param {string} data.notes the new notes value
 * @return the updated data
 */
export async function updateReturnNotes(
  { commit },
  data: {
    uuid: string;
    notes: string;
  },
) {
  const response = await api.order
    .updateReturnNotes(data)
    .catch((e) => handleError(e, i18n.t('orders.notify.errorUpdateReturn')));

  commit('UPDATE_RETURN_NOTES', { ...response?.data, uuid: data.uuid });
  toasts.success(i18n.t('orders.notify.orderReturnUpdated'));

  return response;
}

/**
 * Transitions a return order
 * @param {string} [data.uuid] of the order to be transitioned
 * @param {string} [data.state] of which the order to be transitioned to
 */
export async function transitionOrderReturn({ commit }, data: { uuid: string; state: string; expand?: string[] }) {
  const response = await api.order
    .transitionOrderReturn(data)
    .catch((e) => handleError(e, i18n.t('orders.notify.errorUpdateReturn')));

  commit('SAVE_ORDER_RETURN', response?.data);
  const companyStore = useCompanyStore();

  if ((Object.values(ORDER_RETURN_REVERSE_TRANSITIONS) as string[]).includes(data.state)) {
    companyStore.badgeCounts.submittedReturns++;
  } else {
    companyStore.badgeCounts.submittedReturns--;
  }

  toasts.success(i18n.t('orders.notify.orderReturnUpdated'));
}

/**
 * Fetch Payment Methods
 */
export const fetchPaymentMethods = async ({ commit }) => {
  const paymentMethods = await api.order.getPaymentTypes();

  commit('SET_PAYMENT_METHODS', paymentMethods);

  return paymentMethods;
};

/**
 * Updates an order's payments
 */
export async function updateOrderPayment(_obj, data: OrderPayment) {
  const orderPayment = await api.order.updateOrderPayment(data);

  toasts.success(i18n.t('orders.notify.paymentSubmitted'));

  return orderPayment;
}

/**
 * Fetches a shipping manifest for an order
 */
export async function fetchShippingManifest(_obj, id) {
  const response = await api.order.getShippingManifest(id);

  return response.data;
}

/**
 * Generates a shipping manifest for an order
 */
export async function createShippingManifest(_obj, data) {
  const { isExisting, ...payload } = data;

  let response;

  if (isExisting) {
    response = await api.order.updateShippingManifest(payload);
  } else {
    response = await api.order.addShippingManifest(payload);
  }

  return response.data;
}

/**
 * Updates an order as delivered
 * @param {object} data Payload object
 * @param {boolean} data.buyerPaid If this order is marked as paid by the buyer
 * @param {string} data.deliveredDate Date this order been delivered
 * @param {string} data.uuid Order id/number
 */
export async function updateOrderDelivered(_obj, data: UpdateOrderDeliveredPayload) {
  await api.order.updateOrderDelivered(data);

  toasts.success(i18n.t('orders.notify.orderDeliveryDateUpdated'));
}

/**
 * Fetch order comments
 */
export async function fetchOrderComments({ commit }, data: OrderSummaryComment) {
  const orderComments = await api.order.getOrderComments(data);

  commit('SET_ORDER_COMMENTS', orderComments.data.results);
}

/**
 * Post order comments
 */
export async function postOrderComments({ commit }, data: OrderSummaryComment) {
  await api.order.postOrderComments(data);
  commit('PUSH_COMMENT', data);

  toasts.success(i18n.t('orders.notify.orderCommentsSubmitted'));
}

/**
 * Fetch order documents
 */
export const fetchOrderDocuments = async ({ commit }, uuid: string) => {
  const response = await api.order.getOrderDocuments(uuid);

  commit('SET_ORDER_DOCUMENTS', response.results);

  return response.results;
};

export const createOrderDocument = async ({ commit, state }, payload: OrderDocumentPayload) => {
  const response = await api.order.createOrderDocument(payload).catch((error) => {
    handleError(error, i18n.t('orders.notify.errorAddingDocument'));
  });

  const document: OrderDocument = new JSONTransform(response).toCamelCaseKeys();

  commit('SET_ORDER_DOCUMENTS', [...state.orderDocuments, document]);
  toasts.success(i18n.t('orders.notify.orderDocumentAdded'));

  return response;
};

export const deleteOrderDocument = async ({ commit, state }, payload: OrderDocumentDelete) => {
  await api.order.deleteOrderDocument(payload).catch((error) => {
    handleError(error, i18n.t('orders.notify.errorDeletingDocument'));
  });

  const documents = state.orderDocuments.filter(({ id }) => id !== payload.id);

  if (documents.length < state.orderDocuments.length) {
    commit('SET_ORDER_DOCUMENTS', documents);
  }

  toasts.success(i18n.t('orders.notify.orderDocumentDeleted'));
  return;
};

/**
 * Sets selected facility from OrderCreate in state
 */
export const setSelectedFacility = ({ commit }: ActionContext<OrderState, RootState>, facility: InventoryFacility) => {
  commit('SET_SELECTED_FACILITY', facility);
};
